
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PieChart, PieChartIcon, BarChart3, BriefcaseIcon, ArrowUpRight, Filter, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Portfolio = () => {
  const [timeRange, setTimeRange] = useState("all");
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Portfolio Analysis</h1>
        <PieChartIcon className="h-8 w-8 text-primary" />
      </div>
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold">Portfolio Overview</h2>
          <p className="text-muted-foreground">Analysis of your entire loan portfolio</p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <Select defaultValue={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3m">Last 3 Months</SelectItem>
              <SelectItem value="6m">Last 6 Months</SelectItem>
              <SelectItem value="1y">Last Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon">
            <Filter className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Portfolio Distribution</CardTitle>
            <CardDescription>Breakdown by loan type and sector</CardDescription>
          </CardHeader>
          <CardContent className="h-72 flex items-center justify-center">
            <div className="text-center">
              <PieChart className="h-16 w-16 text-muted-foreground mx-auto" />
              <p className="mt-4 text-muted-foreground">
                Portfolio distribution chart would appear here
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Portfolio Metrics</CardTitle>
            <CardDescription>Key performance indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span>Total Active Loans</span>
                  <span className="font-medium">154</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Total Portfolio Value</span>
                  <span className="font-medium">$2.7M</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Average Loan Size</span>
                  <span className="font-medium">$17,532</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Weighted Avg. Interest</span>
                  <span className="font-medium">5.8%</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span>Default Rate</span>
                  <span className="font-medium">2.3%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="diversification">
        <TabsList>
          <TabsTrigger value="diversification">Diversification</TabsTrigger>
          <TabsTrigger value="risk">Risk Analysis</TabsTrigger>
          <TabsTrigger value="returns">Returns</TabsTrigger>
        </TabsList>
        
        <TabsContent value="diversification" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Sector Allocation</CardTitle>
                <CardDescription>Distribution across business sectors</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Real Estate</span>
                      <span className="text-sm font-medium">35%</span>
                    </div>
                    <Progress value={35} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Small Business</span>
                      <span className="text-sm font-medium">25%</span>
                    </div>
                    <Progress value={25} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Education</span>
                      <span className="text-sm font-medium">15%</span>
                    </div>
                    <Progress value={15} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Healthcare</span>
                      <span className="text-sm font-medium">10%</span>
                    </div>
                    <Progress value={10} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Technology</span>
                      <span className="text-sm font-medium">8%</span>
                    </div>
                    <Progress value={8} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm">Others</span>
                      <span className="text-sm font-medium">7%</span>
                    </div>
                    <Progress value={7} className="h-2" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Geographic Distribution</CardTitle>
                <CardDescription>Loans by region</CardDescription>
              </CardHeader>
              <CardContent className="h-80 flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto" />
                  <p className="mt-4 text-muted-foreground">
                    Geographic distribution chart would appear here
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="risk" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Risk Analysis</CardTitle>
              <CardDescription>Portfolio risk distribution</CardDescription>
            </CardHeader>
            <CardContent className="h-80 w-full flex items-center justify-center">
              <div className="text-center">
                <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Risk analysis charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="returns" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Returns Analysis</CardTitle>
              <CardDescription>Performance of your portfolio</CardDescription>
            </CardHeader>
            <CardContent className="h-80 w-full flex items-center justify-center">
              <div className="text-center">
                <BriefcaseIcon className="h-16 w-16 text-muted-foreground mx-auto" />
                <p className="mt-4 text-muted-foreground">
                  Returns analysis charts would appear here
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button className="flex items-center gap-2">
          Portfolio Optimization <ArrowUpRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default Portfolio;
